import type { PageParams, PaginationData } from './../../../types/api';
import { request, type IRequestConfig } from '@/api';
import type { UserInfo } from '@/stores/userStore';

const PREFIX = '/user';

export type ChangePasswordData = {
  /**
   * 确认密码
   */
  confirmPassword: string;
  /**
   * 新密码
   */
  newPassword: string;
  /**
   * 风险操作验证获取的临时令牌
   */
  token: string;
};

export type BindEmailData = {
  /**
   * 邮箱
   */
  email: string;
  /**
   * 邮箱验证码
   */
  emailCode: string;
  /**
   * 风险操作验证获取的临时令牌
   */
  token: string;
};

export type BindPhoneData = {
  /**
   * 手机号
   */
  phone: string;
  /**
   * 手机号验证码
   */
  smsCode: string;
  /**
   * 风险操作验证获取的临时令牌
   */
  token: string;
};

export type UserInfoRes = Omit<UserInfo, 'clientId' | 'permissionCodes' | 'roleKeys' | 'roleNames'>;

export type UserQRListFilter = {
  /**
   * 创建结束时间。
   */
  createTimeEnd?: string;
  /**
   * 创建开始时间。
   */
  createTimeStart?: string;
  /**
   * 启停状态：0停用，1启用。
   */
  enabledStatus?: number;
  /**
   * 展示状态：0不存在，1正常，2已停用，3已失效，4次数已用尽。
   */
  displayStatus?: number[];
  /**
   * 所属用户ID。
   */
  ownerUserId: string;
  /**
   * 二维码名称。
   */
  qrCodeName?: string;
};

export type ManagementUserQrCodeVO = {
  /**
   * 创建时间。
   */
  createTime?: string;
  /**
   * 展示状态：0不存在，1正常，2已停用，3已失效，4次数已用尽。
   */
  displayStatus?: number;
  /**
   * 展示状态名称。
   */
  displayStatusName?: string;
  /**
   * 启停状态：0停用，1启用。
   */
  enabledStatus?: number;
  /**
   * Management 用户二维码ID。
   */
  managementUserQrCodeId?: string;
  /**
   * 所属用户昵称。
   */
  ownerNickname?: string;
  /**
   * 所属用户ID。
   */
  ownerUserId?: number;
  /**
   * 二维码名称。
   */
  qrCodeName?: string;
  /**
   * 二维码编号。
   */
  qrCodeNo?: string;
  /**
   * 二维码图片 OSS Key。
   */
  qrImageOssKey?: string;
  /**
   * 二维码图片访问地址。
   */
  qrImageUrl?: string;
  /**
   * 总有效次数。
   */
  totalValidCount?: number;
  /**
   * 已使用次数。
   */
  usedCount?: number;
  /**
   * 有效期截止时间。
   */
  validUntil?: string;
};

export type AddQRData = {
  /**
   * 二维码所属用户ID。
   */
  ownerUserId: string;
  /**
   * 二维码名称。
   */
  qrCodeName: string;
  /**
   * 总有效次数。
   */
  totalValidCount: number;
  /**
   * 有效期截止时间。
   */
  validUntil: string;
};

/**
 * 获取用户信息
 */
export const getUserInfo = () => {
  return request.get<UserInfo>(`${PREFIX}/detail`);
};

/**
 * /nickname/change
 * 变更昵称
 */
export const changeNickname = (nickname: string) => {
  return request.post(`${PREFIX}/nickname/change`, { nickname });
};

/**
 * /password/change
 * 修改密码
 */
export const changePassword = (data: ChangePasswordData) => {
  return request.post(`${PREFIX}/password/change`, data);
};

/**
 * /email/bind
 * 绑定邮箱
 */
export const bindEmail = (data: BindEmailData) => {
  return request.post(`${PREFIX}/email/bind`, data);
};

/**
 * /email/bind/code/send
 * 发送邮箱验证码
 * @param token 风险操作验证获取的临时令牌
 * @param email 待绑定邮箱
 */
export const sendBindEmailCode = (token: string, email: string) => {
  return request.post(`${PREFIX}/email/bind/code/send`, { token, email });
};

/**
 * /phone/bind
 * 绑定手机号
 */
export const bindPhone = (data: BindPhoneData) => {
  return request.post(`${PREFIX}/phone/bind`, data);
};

/**
 * /phone/bind/code/send
 * 发送手机号验证码
 * @param token 风险操作验证获取的临时令牌
 * @param phone 待绑定手机号
 */
export const sendBindPhoneCode = (token: string, phone: string) => {
  return request.post(`${PREFIX}/phone/bind/code/send`, { token, phone });
};

/**
 * 获取用户详情
 * /detail
 */
export const getUserDetail = () => {
  return request.get<UserInfo>(`${PREFIX}/detail`);
};

/**
 * 二维码管理分页
 * /management/user/qr/page
 */
export const getUserQRList = (data: PageParams<UserQRListFilter>, config?: IRequestConfig) => {
  return request.post<PaginationData<ManagementUserQrCodeVO>>(
    `/management/user/qr/page`,
    data,
    config
  );
};

/**
 * 新增二维码
 * /management/user/qr/add
 */
export const addQR = (data: AddQRData) => {
  return request.post(`/management/user/qr/add`, data);
};

/**
 * 停用二维码
 * /management/user/qr/disable
 */
export const disableQR = (managementUserQrCodeId: string) => {
  return request.post(`/management/user/qr/disable`, { managementUserQrCodeId });
};
